import Image from 'next/image'
import Button from '../Button'
import {useQuery} from 'react-query'
import GetCovers from '@/services/api/personalize/GetCovers'
import React, {Dispatch, SetStateAction} from 'react'
import CustomToast from '../CustomToast'
import toast from 'react-hot-toast'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useCurrency } from '../DesktopHeader'
import useUserPreferences from '@/hooks/useUserPreferences'

interface Props {
  product: any
  inputCover: any
  setInputCover: Dispatch<SetStateAction<string | undefined>>
  SubmitChange?: any
  close?: any
  isEdit?: any,
  setCoverModal?: any,
  updateProductData?: (data: any) => void, // Fixed: explicitly typed
  handleSave?: () => void,
  markOptionCompleted?: (id: string, completed: boolean) => void,
  userCurrency?: any,
  setCoverType?: Dispatch<SetStateAction<string>>
}

export default function CoversSelection({
  product,
  inputCover,
  SubmitChange,
  setInputCover,
  close = undefined,
  isEdit,
  setCoverModal,
  updateProductData,
  handleSave,
  markOptionCompleted,
  userCurrency,
  setCoverType
}: Props) {
  const {t} = useTranslation('personalizeModal');
  const {locale} = useRouter();
  const product_id = isEdit === '1' ? product?.product_id : product?.id;
  const {data, isLoading} = useQuery(['covers', product_id, userCurrency], GetCovers)
  const preferences = useUserPreferences();
  const currencySymbol = useCurrency(preferences?.currencyCode || 'ILS');

  React.useEffect(() => {
    if (isEdit === '1' && product?.cover_id) {
      setInputCover(product.cover_id);
      if (setCoverType) {
        setCoverType('');
      }
    }
  }, [isEdit, product?.cover_id, setInputCover, setCoverType]);

  const handleCoverSave = () => {
    if (!inputCover || inputCover === '' || inputCover === undefined) {
      toast.custom((tEl) => <CustomToast t={tEl} message={`${t('coverSelectionError')}`} type="error" />);
      return;
    }
    
    // FIX: Check if updateProductData exists and is a function before calling
    if (updateProductData && typeof updateProductData === 'function') {
      updateProductData({cover: inputCover});
    }
    
    // Mark option as completed
    if (markOptionCompleted && typeof markOptionCompleted === 'function') {
      markOptionCompleted('cover', true);
    }
    
    // Save changes - handleSave is required for edit mode
    if (handleSave && typeof handleSave === 'function') {
      handleSave();
    } else if (SubmitChange && typeof SubmitChange === 'function') {
      // Fallback for non-edit mode
      SubmitChange();
    }
    
    // Close modal if needed
    if (setCoverModal && typeof setCoverModal === 'function') {
      setCoverModal(false);
    }
  };

  return (
    <div className="personalization-content covers-selection items-center w-full xl:mx-auto xl:max-w-xl px-4" dir={`${locale == 'ar' ?'rtl': 'ltr'}`}>
      <h3 className={`block lg:hidden text-base font-bold ${locale === 'ar' ? 'text-right':'text-left'}`}>{t('coverSelectionHeading')}</h3>
      <div className='hidden lg:flex w-full items-center justify-between lg:mb-3'>
        <h3 className="text-base font-bold">{t('coverSelectionHeading')}</h3>
        <button 
            className="bg-[#93A3B03D] text-xl p-2 rounded-md" 
            onClick={()=>{
              if (setCoverModal && typeof setCoverModal === 'function') {
                setCoverModal(false);
              }
            }}
          >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8.23742 7.00023L13.7439 1.88765C14.0857 1.57026 14.0857 1.05566 13.7439 0.738291C13.4021 0.420895 12.8479 0.420895 12.5061 0.738291L7.00022 5.8514L1.49438 0.738291C1.15257 0.420895 0.598385 0.420895 0.256602 0.738291C-0.0851811 1.05569 -0.0852084 1.57028 0.256602 1.88765L5.76302 7.00023L0.256602 12.1128C-0.0852084 12.4302 -0.0852084 12.9448 0.256602 13.2622C0.598412 13.5796 1.15259 13.5796 1.49438 13.2622L7.00022 8.14905L12.5061 13.2622C12.8479 13.5796 13.4021 13.5796 13.7438 13.2622C14.0857 12.9448 14.0857 12.4302 13.7438 12.1128L8.23742 7.00023Z" fill="black"/>
          </svg>
        </button>
      </div>
      {!isLoading ? 
      (
        <div className="covers-list w-full mt-2 flex flex-col mx-auto gap-2">
        {[...data]?.reverse().map((cover: any, index: number) => {
          return (
            <a
              href="#"
              key={cover.id}
              id="cover-item"
              onClick={e => {
                e.preventDefault()
                setInputCover(cover.id)
                if (setCoverType) {
                  setCoverType(cover?.type);
                }
              }}
              className={`cover transition duration-200 mt-1 lg:mt-0 cursor-pointer border-2 flex flex-col p-4 rounded-lg lg:items-start lg:w-full bg-white ${
                (inputCover == cover.id) ? 'border-primary' : ''
              } gap-1`}>
              <div className="cover-image h-[120px] lg:h-[150px] lg:w-full flex items-center justify-center bg-gray-200">
                <Image
                  src={
                    cover.type === '1' ? '/assets/svgImages/Hard cover.svg' : '/assets/svgImages/Soft Cover.svg'
                  }
                  width={200}
                  height={150}
                  alt={cover.name}
                  unoptimized
                  priority
                  className={'w-full h-full max-h-[120px] lg:max-h-[150px] rounded-md object-cover'}
                />
              </div>
              <h4 className={`mt-4 text-base font-medium ${locale === 'ar' ? 'text-right': 'text-left'}`}>{locale === 'ar' ? cover.name?.ar : cover.name?.en}</h4>
              <p className={`text-sm text-text-color font-normal ${locale === 'ar' ? 'text-right': 'text-left'}`}>
                {locale === 'ar' ? cover.description?.ar : cover.description?.en}
              </p>
              <strong className={`price text-red text-2xl font-bold mt-1 ${locale === 'ar' ? 'text-right': 'text-left'}`}>{cover.price?.amount} {currencySymbol.symbol}</strong>
            </a>
          )
        })}
      </div>
      )
      : 
      (
      <div className='flex-1 overflow-y-auto px-4 py-6 bg-[#FFFCEB] flex items-center justify-center gap-4'>
        <div className="w-6 h-6 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        <h2 className="text-lg font-semibold leading-none">{t('coverSelectionLoading')}</h2>
      </div>
      )}

      <Button
        type="button"
        bg="primary"
        id="covers-continue-button"
        onClick={handleCoverSave}
        classes={['py-3', 'w-full', 'mt-4', 'font-semibold', 'hidden', 'lg:block', 'lg:mt-6']}>
        {`${t('continue')}`}
      </Button>
    </div>
  )
}